import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckEnablingsConfig } from '@core/models/built/procedure-step-config.model';
import { CheckStrategy } from '@core/models/check-strategy.enum';
import { Enabling } from '@core/models/enabling.model';
import { ProcedureStep } from '@core/models/procedures.model';
import { EnablingCoreService } from '@core/services/features/enabling-core.service';
import { FlowchartMode } from '@flowchart';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { concatMap, first } from 'rxjs/operators';
import { getProcedureStepTypeConfigByType } from 'src/app/modules/core/models/procedure-step-config-map';

import it from './i18n/check-enablings-edit.it.json';

@Component({
  templateUrl: './check-enablings-edit.component.html'
})
export class CheckEnablingsEditComponent implements OnInit, OnDestroy {

  @Input() step?: ProcedureStep;
  @Input() mode: FlowchartMode = 'edit';
  @Input() editForm!: FormGroup;

  private enablings$$ = new BehaviorSubject<Enabling[]>([]);
  enablings$ = this.enablings$$.asObservable();

  checkStrategies: string[] = Object.keys(CheckStrategy);

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private enablingCoreSvc: EnablingCoreService,
    private translocoSvc: TranslocoService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {

    this.handleTypeChange();

    const config = this.step?.config as CheckEnablingsConfig;

    const configGroup = (this.editForm.get('config') as FormGroup);
    if (configGroup) {
      configGroup.addControl('enablingIds', this.fb.array([], [Validators.minLength(1)]));
      configGroup.addControl('checkStrategy', this.fb.control(config?.checkStrategy, [Validators.required]));
    } else {
      this.editForm.addControl('config', this.fb.group({
        enablingIds: this.fb.array([], [Validators.minLength(1)]),
        checkStrategy: [config?.checkStrategy, [Validators.required]]
      }));
    }

    if (config?.enablingIds && config?.enablingIds.length > 0) {
      for (const id of config?.enablingIds) {
        this.addEnabling(this.editForm, id);
      }
    } else {
      this.addEnabling(this.editForm);
    }

    if (this.mode === 'view') {
      (this.editForm.get('config') as FormGroup).disable();
    }
  }

  private handleTypeChange() {
    const enablingType = getProcedureStepTypeConfigByType(this.editForm.controls.type.value)?.enablingType;
    if (enablingType) {
      this.enablingCoreSvc.getListByType(enablingType).pipe(first()).subscribe(
        enablings => this.enablings$$.next(enablings)
      );
    }

    this.subs.add(
      this.editForm.controls.type.valueChanges.pipe(
        concatMap(type => {
          const eType = getProcedureStepTypeConfigByType(type)?.enablingType;
          if (eType) {
            return this.enablingCoreSvc.getListByType(eType);
          } else {
            return of([]);
          }
        })
      ).subscribe(enablings => this.enablings$$.next(enablings))
    );
  }

  ngOnDestroy() {
    const configGroup = (this.editForm.get('config') as FormGroup);
    if (configGroup) {
      configGroup.removeControl('enablingIds');
      configGroup.removeControl('checkStrategy');

      if (Object.keys(configGroup.controls).length <= 0) {
        this.editForm.removeControl('config');
      }
    }

    this.subs.unsubscribe();
  }

  addEnabling(form: FormGroup, value?: number) {
    (form.get('config.enablingIds') as FormArray).push(this.fb.control(value, [Validators.required]));
  }

  removeEnabling(index: number) {
    const fa = this.editForm.get('config.enablingIds') as FormArray;
    if (fa.controls.length > 1) {
      fa.removeAt(index);
    }
  }

  getEnablingIdsControls() {
    return (this.editForm.get('config.enablingIds') as FormArray)?.controls as FormControl[] || [];
  }
}
